import { PostingArrivalSurface } from '@/features/notifications/components/PostingArrivalSurface';
import { Card, CardContent } from '@/components/ui/card';
import { PostingRecoveryContent } from '@shared/posting/PostingRecoveryContent';
import { salesOrderRecoveryView } from '@shared/posting/postingRecovery';
import type { OutboxPostingStatusDto } from '@shared/posting';

interface Props {
  /** 86d3mnuxe — the in-flight ("Posting") / dead-lettered ("Failed") outbox job for this SO, from
   *  fetchOutboxStatus(salesorder:{id}). null → no posting job (Draft/Submitted) → renders nothing. */
  posting: OutboxPostingStatusDto | null;
  /** 86d3mnuxe funnel — NotReached Retry: cancel the failed intent, then re-claim (a genuine
   *  re-decision; the drift dialog may fire). Hidden on a reached failure. */
  onRetry: () => void;
  /** 86d3mnuxe funnel — Reconcile a REACHED failure: re-arms the same job so the frozen decision
   *  re-executes and the adopt records the SO NetSuite already accepted. Never prompts. */
  onReconcile?: () => void;
  /** 86d3mnuxe funnel — close a NotReached failure (Failed → Cancelled) without re-posting; the
   *  draft then accepts a fresh submit. Submitter-level (same permission as the post). Hidden on a
   *  reached failure (the BE refuses it too — abandoning would orphan the record). */
  onCancel?: (() => void) | null;
  isBusy: boolean;
}

/**
 * 86d3mnuxe — top-of-detail card driven by the OUTBOX status (the posting lifecycle moved off the
 * entity). Returns null when there is no active posting job.
 *
 * N8 (86d3w2kez): this is now CHROME ONLY. The button set, the reach disclosure and every line of
 * copy come from the shared `PostingRecoveryContent`, so this card, the cutting-list card, the MIS
 * batch banner, the IR banner and the floating recovery panel cannot drift apart. They had already
 * drifted once — two of them hand-rolled `postReach >= 2` instead of calling the shared predicate,
 * and the cost of that decision being wrong is a duplicate sales order in the live company file.
 * ⛔ Do not reintroduce reach logic here.
 */
export function SalesOrderPostingStatusCard({
  posting,
  onRetry,
  onReconcile,
  onCancel,
  isBusy,
}: Props) {
  if (posting === null) return null;

  const isFailed = posting.status === 'Failed';
  const view = salesOrderRecoveryView(posting);

  // N8: wrapped HERE rather than at each mount site — this card renders from the workspace detail
  // panel and from the compose page, and the arrival behaviour should not depend on which one a
  // notification happened to land on.
  return (
    <PostingArrivalSurface
      views={[view]}
      onRetry={onRetry}
      onReconcile={onReconcile}
      onCancel={onCancel ?? undefined}
      isBusy={isBusy}
    >
      <Card
        className={isFailed ? 'border-destructive/30 bg-destructive/10' : 'border-info/30 bg-info/10'}
      >
        <CardContent className="py-4">
          <PostingRecoveryContent
            view={view}
            onRetry={onRetry}
            onReconcile={onReconcile}
            onCancel={onCancel}
            isBusy={isBusy}
          />
        </CardContent>
      </Card>
    </PostingArrivalSurface>
  );
}
